import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormButton, FormCheckboxGroup, FormRadioButtonGroups, FormTextInput } from 'thermia-react-container';
import { COOLANTS } from '../../../../../types/coolantTypes';
import { HEAT_SOURCES } from '../../../../../types/heatSource';
import { HEAT_SYSTEMS } from '../../../../../types/heatSystems';
import { INSTALLATION_TYPE, InstallationTypeCode } from '../../../../../types/installationType';
import { Registration } from '../../../../../types/registrationTypes';
import CompleteRegistrationButton from '../../../components/completeRegistrationButton';
import { StyledContainer } from '../../../registration';
import SystemYup from '../../../../../validation/yup-input/registrationSystem';

type Props = {
  heatingSetup?: Registration['heatingSetup'];
  updateRegistration: (registration: Registration['heatingSetup']) => Promise<void>;
  preventSwitchTabDueToUnsavedData: (value: boolean) => void;
  loading: boolean;
  disabled: boolean;
};

const SystemForm: FC<Props> = ({ heatingSetup, updateRegistration, loading, preventSwitchTabDueToUnsavedData, disabled }) => {
  const { t } = useTranslation('registrations');

  const schema = SystemYup();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm({
    defaultValues: {
      heatSource: heatingSetup?.heatSource ?? [],
      heatSystem: heatingSetup?.heatSystem ?? [],
      coolant: heatingSetup?.coolant ?? '',
      installationType: heatingSetup?.installationType ?? InstallationTypeCode.newConstruction,
      comment: heatingSetup?.comment ?? ''
    },
    mode: 'onBlur',
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    preventSwitchTabDueToUnsavedData(isDirty);
  }, [isDirty, preventSwitchTabDueToUnsavedData]);

  const onValid = async (data: any) => {
    const updatedHeatingSetup: Registration['heatingSetup'] = {
      heatSource: data.heatSource,
      heatSystem: data.heatSystem,
      coolant: data.coolant,
      installationType: data.installationType
    };

    if (data.comment !== '') {
      updatedHeatingSetup.comment = data.comment;
    }
    await updateRegistration(updatedHeatingSetup);
    preventSwitchTabDueToUnsavedData(false);

    reset({
      heatSource: updatedHeatingSetup.heatSource,
      heatSystem: updatedHeatingSetup.heatSystem,
      coolant: updatedHeatingSetup.coolant,
      installationType: updatedHeatingSetup.installationType,
      comment: updatedHeatingSetup.comment ?? ''
    });
  };
  return (
    <>
      <StyledContainer>
        <Grid container>
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={3}>
              <FormRadioButtonGroups
                isDisabled={disabled}
                control={control}
                name={'installationType'}
                label={t('registrations.general.installationType')}
                options={INSTALLATION_TYPE.map((coolant) => ({
                  label: t(`registrations.general.${coolant.code}`),
                  value: coolant.code
                }))}
              />
            </Grid>
            <Grid item xs={3}>
              <FormCheckboxGroup
                disabled={disabled}
                control={control}
                name={'heatSource'}
                label={t('registrations.general.heatSource')}
                options={HEAT_SOURCES.map((heatSource) => ({
                  label: t(`registrations.general.${heatSource.code}`),
                  value: heatSource.code
                }))}
              />
            </Grid>
            <Grid item xs={3}>
              <FormCheckboxGroup
                disabled={disabled}
                control={control}
                name={'heatSystem'}
                label={t('registrations.general.heatSystem')}
                options={HEAT_SYSTEMS.map((heatSystem) => ({
                  label: t(`registrations.general.${heatSystem.code}`),
                  value: heatSystem.code
                }))}
              />
            </Grid>
            <Grid item xs={3}>
              <FormRadioButtonGroups
                isDisabled={disabled}
                control={control}
                name={'coolant'}
                label={t('registrations.general.coolant')}
                options={COOLANTS.map((coolant) => ({
                  label: t(`registrations.general.${coolant.code}`),
                  value: coolant.code
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextInput disabled={disabled} control={control} multiline name="comment" label={t('registrations.general.comment')} />
            </Grid>
          </Grid>
        </Grid>
      </StyledContainer>
      {!disabled && (
        <Grid container spacing={2} justifyContent={'end'} alignItems={'center'} marginTop={2}>
          <Grid item>
            <FormButton
              variant={'contained'}
              onClick={handleSubmit(onValid)}
              title={t('registrations.general.save')}
              loading={loading}
            />
          </Grid>
          <Grid item>
            <CompleteRegistrationButton formIsDirty={isDirty} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SystemForm;
